import React, { useEffect, useRef } from 'react';
import { FinancialsTableRow } from './FinancialsTableRow';
import PeriodRow from './PeriodRow';
import { renameKey } from '../../types/KeyMappingType';

interface FinancialDataItem {
    [key: string]: any;
}

interface FinancialsTableProps {
    financial_data: FinancialDataItem[];
}

export const FinancialsTable: React.FC<FinancialsTableProps> = ({financial_data}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
    }, [financial_data]);

    financial_data.sort((a, b) => {
        // Convert date strings to date objects for comparison
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
      
        return dateA - dateB; // Sorts in ascending order
      });


    const period_endings = financial_data.map(item => item.date);
    const uniqueKeys = Array.from(new Set(financial_data.flatMap(Object.keys)));
    function extractValuesForKey<T = any>(objectsArray: Record<string, T>[], key: string): T[] {
        return objectsArray.map(obj => obj[key]);
    }
    
    const skipKeys = ['date', 'calendarYear', 'symbol', 'reportedCurrency', 'cik', 'fillingDate', 'acceptedDate', 'period', 'link', 'finalLink']
    const filteredKeys = uniqueKeys.filter(key => !skipKeys.includes(key));
    const keyValues = filteredKeys.map(key => extractValuesForKey(financial_data, key));
    const keysThatAreWholeCurrency = ['revenue', 'costOfRevenue', 'grossProfit', 'researchAndDevelopmentExpenses', 'generalAndAdministrativeExpenses', 'sellingAndMarketingExpenses', 'sellingGeneralAndAdministrativeExpenses', 'otherExpenses', 'operatingExpenses', 'costAndExpenses', 'interestIncome', 'interestExpense', 'depreciationAndAmortization', 'ebitda', 'operatingIncome', 'totalOtherIncomeExpensesNet', 'incomeBeforeTax', 'incomeTaxExpense', 'netIncome', 'deferredIncomeTax', 'stockBasedCompensation', 'changeInWorkingCapital', 'accountsReceivables', 'inventory', 'accountsPayables', 'otherWorkingCapital', 'otherNonCashItems', 'netCashProvidedByOperatingActivities', 'investmentsInPropertyPlantAndEquipment', 'acquisitionsNet', 'purchasesOfInvestments', 'salesMaturitiesOfInvestments', 'otherInvestingActivites', 'netCashUsedForInvestingActivites', 'debtRepayment', 'commonStockIssued', 'commonStockRepurchased', 'dividendsPaid', 'otherFinancingActivites', 'netCashUsedProvidedByFinancingActivities', 'effectOfForexChangesOnCash', 'netChangeInCash', 'cashAtEndOfPeriod', 'cashAtBeginningOfPeriod', 'operatingCashFlow', 'capitalExpenditure', 'freeCashFlow', 'cashAndCashEquivalents', 'shortTermInvestments', 'cashAndShortTermInvestments', 'netReceivables', 'otherCurrentAssets', 'totalCurrentAssets', 'propertyPlantEquipmentNet', 'goodwill', 'intangibleAssets', 'goodwillAndIntangibleAssets', 'longTermInvestments', 'taxAssets', 'otherNonCurrentAssets', 'totalNonCurrentAssets', 'otherAssets', 'totalAssets', 'accountPayables', 'shortTermDebt', 'taxPayables', 'deferredRevenue', 'otherCurrentLiabilities', 'totalCurrentLiabilities', 'longTermDebt', 'deferredRevenueNonCurrent', 'deferredTaxLiabilitiesNonCurrent', 'otherNonCurrentLiabilities', 'totalNonCurrentLiabilities', 'otherLiabilities', 'capitalLeaseObligations', 'totalLiabilities', 'preferredStock', 'commonStock', 'retainedEarnings', 'accumulatedOtherComprehensiveIncomeLoss', 'othertotalStockholdersEquity', 'totalStockholdersEquity', 'totalEquity', 'totalLiabilitiesAndStockholdersEquity', 'minorityInterest', 'totalLiabilitiesAndTotalEquity', 'totalInvestments', 'totalDebt', 'netDebt']
    const keysThatAreFractionalCurrency = ['eps', 'epsDiluted', 'epsdiluted']
    const keysThatArePercentages = ['grossProfitRatio', 'ebitdaratio', 'operatingIncomeRatio', 'incomeBeforeTaxRatio', 'netIncomeRatio']
    const keysThatAreIntegers = ['weightedAverageShsOut', 'weightedAverageShsOutDil']

    function formatAsWholeCurrency(valueStr: string): string {
        const value = parseFloat(valueStr);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    function formatAsFractionalCurrency(valueStr: string): string {
        const value = parseFloat(valueStr);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,

        }).format(value);
    }

    function formatAsPercent(valueStr: string): string {
        const value = parseFloat(valueStr);
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            maximumFractionDigits: 2,
        }).format(value);
    }

    function formatAsInteger(valueStr: string): string {
        const value = Number(valueStr);
        return value.toLocaleString('en-US', {
            maximumFractionDigits: 0,
        })
    }

    keyValues.forEach((values, index) => {
        values.forEach((value, i) => {
            if (keysThatAreWholeCurrency.includes(filteredKeys[index])) {
                keyValues[index][i] = formatAsWholeCurrency(value);
            } else if (keysThatAreFractionalCurrency.includes(filteredKeys[index])) {
                keyValues[index][i] = formatAsFractionalCurrency(value);
            } else if (keysThatArePercentages.includes(filteredKeys[index])) {
                keyValues[index][i] = formatAsPercent(value);
            } else if (keysThatAreIntegers.includes(filteredKeys[index])) {
                keyValues[index][i] = formatAsInteger(value);
            }
        })
    })

    const formattedData = filteredKeys.map((key, index) => ({
        key: renameKey(key),
        values: keyValues[index]
    }));

    return <>
        <div ref={scrollContainerRef} className="overflow-x-auto overflow-y-auto max-h-[800px] mt-4 custom-scrollbar">
            <table className="w-full text-md text-right text-gray-500 table-fixed">
                <thead className="uppercase bg-white">
                    <PeriodRow row_title="PERIOD ENDING" periods={period_endings}/>
                </thead>
                <tbody>
                {formattedData.map((item) => (
                        <FinancialsTableRow 
                            key={item.key} 
                            row_title={item.key} 
                            values={item.values}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    </>
};
